/**
 * WarningAreaLayer Component
 *
 * Renders warning zone polygons on the map showing affected areas for fire warnings.
 * These are separate from actual burn perimeters (shown in FireBoundaryLayer).
 *
 * Warning zones indicate areas where residents should be alert, not necessarily
 * areas that have already burned.
 *
 * PERFORMANCE: Only renders boundaries that intersect with the current map viewport.
 */

import { useState, useEffect, useMemo, memo, useRef } from 'react';
import { Polygon, Pane, useMap } from 'react-leaflet';
import { UnifiedEvent } from '../types/events';
import { LatLngExpression } from 'leaflet';

// Custom pane name for warning areas - rendered below fire boundaries
const WARNING_AREA_PANE = 'warningAreaPane';
const WARNING_AREA_PANE_Z = 340; // Below fire boundaries (350)

interface WarningAreaLayerProps {
  events: UnifiedEvent[];
}

/**
 * Warning area colors based on severity level.
 * Uses semi-transparent fills to show affected zones.
 */
const WARNING_COLORS: Record<string, { fill: string; stroke: string }> = {
  EMERGENCY: { fill: '#dc2626', stroke: '#991b1b' },      // Red
  WATCH_AND_ACT: { fill: '#f97316', stroke: '#c2410c' },  // Orange
  ADVICE: { fill: '#fbbf24', stroke: '#d97706' },         // Yellow
  NOT_APPLICABLE: { fill: '#6b7280', stroke: '#4b5563' }, // Gray
  HEALTH: { fill: '#8b5cf6', stroke: '#6d28d9' },         // Purple for health advisories
};

/**
 * Convert GeoJSON polygon coordinates to Leaflet LatLng format.
 * GeoJSON uses [lon, lat] but Leaflet uses [lat, lon].
 */
function convertCoordinates(coords: number[][]): LatLngExpression[] {
  return coords.map(([lon, lat]) => [lat, lon] as LatLngExpression);
}

/**
 * Extract polygon positions from boundary geometry.
 * Handles both Polygon and MultiPolygon types.
 */
function getPolygonPositions(
  geometry: { type: string; coordinates: number[][][] | number[][][][] } | null
): LatLngExpression[][] | null {
  if (!geometry) return null;

  if (geometry.type === 'Polygon') {
    const coords = geometry.coordinates as number[][][];
    return coords.map(ring => convertCoordinates(ring));
  } else if (geometry.type === 'MultiPolygon') {
    const coords = geometry.coordinates as number[][][][];
    return coords.map(polygon => convertCoordinates(polygon[0]));
  }

  return null;
}

/**
 * Calculate bounding box for a polygon geometry.
 */
function getBoundingBox(
  geometry: { type: string; coordinates: number[][][] | number[][][][] } | null
): [number, number, number, number] | null {
  if (!geometry) return null;

  let minLat = Infinity, minLon = Infinity;
  let maxLat = -Infinity, maxLon = -Infinity;

  const processCoords = (coords: number[][]) => {
    for (const [lon, lat] of coords) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
  };

  if (geometry.type === 'Polygon') {
    const coords = geometry.coordinates as number[][][];
    coords.forEach(ring => processCoords(ring));
  } else if (geometry.type === 'MultiPolygon') {
    const coords = geometry.coordinates as number[][][][];
    coords.forEach(polygon => polygon.forEach(ring => processCoords(ring)));
  }

  if (minLat === Infinity) return null;
  return [minLat, minLon, maxLat, maxLon];
}

/**
 * Memoized single polygon renderer with severity-based colors
 */
const WarningPolygon = memo(function WarningPolygon({
  positions,
  eventId,
  severity,
  idx
}: {
  positions: LatLngExpression[] | LatLngExpression[][];
  eventId: string;
  severity: string;
  idx?: number;
}) {
  const colors = WARNING_COLORS[severity] || WARNING_COLORS.NOT_APPLICABLE;

  return (
    <Polygon
      key={idx !== undefined ? `${eventId}-${idx}` : eventId}
      positions={positions}
      pane={WARNING_AREA_PANE}
      pathOptions={{
        color: colors.stroke,
        fillColor: colors.fill,
        fillOpacity: 0.2, // More transparent than burn areas
        weight: 2,
        opacity: 0.7,
        dashArray: '5, 5', // Dashed line to differentiate from burn areas
        interactive: false,
      }}
    />
  );
});

// Minimum zoom level to show warning areas
const MIN_ZOOM_FOR_WARNINGS = 7;

interface StableBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

const WarningAreaLayer = memo(function WarningAreaLayer({ events }: WarningAreaLayerProps) {
  const map = useMap();
  const [stableBounds, setStableBounds] = useState<StableBounds | null>(null);
  const [zoom, setZoom] = useState<number>(map.getZoom());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update view bounds and zoom on map move/zoom with debouncing
  useEffect(() => {
    const updateBounds = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        setStableBounds({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
        setZoom(map.getZoom());
      }, 100);
    };

    // Initial bounds
    const initialTimeout = setTimeout(() => {
      const bounds = map.getBounds();
      setStableBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
      setZoom(map.getZoom());
    }, 100);

    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      clearTimeout(initialTimeout);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter and prepare visible warning zones
  const visibleWarnings = useMemo(() => {
    if (zoom < MIN_ZOOM_FOR_WARNINGS) return [];
    if (!stableBounds) return [];

    return events
      .filter(event => {
        // Look for FIRE_INCIDENT or HEALTH events with warningGeometry
        if ((event.type !== 'FIRE_INCIDENT' && event.type !== 'HEALTH') || !event.data?.warningGeometry) {
          return false;
        }

        const warningGeometry = event.data.warningGeometry as {
          type: string;
          coordinates: number[][][] | number[][][][];
        };

        const bbox = getBoundingBox(warningGeometry);
        if (!bbox) return false;

        const [minLat, minLon, maxLat, maxLon] = bbox;
        return !(maxLat < stableBounds.south || minLat > stableBounds.north ||
                 maxLon < stableBounds.west || minLon > stableBounds.east);
      })
      .map(event => ({
        event,
        geometry: event.data?.warningGeometry as {
          type: string;
          coordinates: number[][][] | number[][][][];
        },
        // For HEALTH events, use 'HEALTH' as severity for purple color
        severity: event.type === 'HEALTH' ? 'HEALTH' : ((event.data?.severity as string) || 'NOT_APPLICABLE'),
      }));
  }, [events, stableBounds?.north, stableBounds?.south, stableBounds?.east, stableBounds?.west, zoom]);

  if (visibleWarnings.length === 0) {
    return null;
  }

  return (
    <Pane name={WARNING_AREA_PANE} style={{ zIndex: WARNING_AREA_PANE_Z }}>
      {visibleWarnings.map(({ event, geometry, severity }) => {
        const positions = getPolygonPositions(geometry);
        if (!positions || positions.length === 0) return null;

        if (geometry.type === 'MultiPolygon') {
          return positions.map((polygonPositions, idx) => (
            <WarningPolygon
              key={`${event.id}-${idx}`}
              positions={polygonPositions}
              eventId={event.id}
              severity={severity}
              idx={idx}
            />
          ));
        }

        return (
          <WarningPolygon
            key={event.id}
            positions={positions}
            eventId={event.id}
            severity={severity}
          />
        );
      })}
    </Pane>
  );
});

export default WarningAreaLayer;
